import { ElementRef, useEffect, useRef, useState } from 'react';

import { Formik } from 'formik';
import { motion } from 'framer-motion';
import Image from 'next/image';
import Link from 'next/link';

import { ISanitizedCompany } from 'types/company';
import { TeamsTab } from 'types/team';
import { IUserResponse } from 'types/user';

import { useLogout } from 'hooks/auth/sign';
import { useFreshCompany } from 'hooks/companies/fresh';
import { useHandleCompanySaved } from 'hooks/companies/saved';

import { CompanyDrawer } from 'components/company/CompanyDrawer';
import { StatisticsIcon } from 'components/creator-toolkit/icons/StatisticsIcon';
import { Menu } from 'components/shared/Menu';
import { Spinner } from 'components/shared/Spinner';
import { ProfileDrawer } from 'components/user/ProfileDrawer';

import imageSrc from 'public/authentication/avatar.png';

import { Drawer } from '../Drawer';
import { CompanyIcon } from '../icons/CompanyIcon';
import { LibraryIcon } from './icons/LibraryIcon';
import { LogoutIcon } from './icons/LogoutIcon';
import { TeamIcon } from './icons/TeamIcon';
import { UserIcon } from './icons/UserIcon';

interface IProps {
  user: IUserResponse;
  canCreateStories: boolean | undefined;
  canEditUsers: boolean | undefined;
  company: ISanitizedCompany;
}

export const UserMenu = ({
  user,
  company,
  canCreateStories,
  canEditUsers,
}: IProps) => {
  const [showAvatar, setShowAvatar] = useState(false);

  const { handleLogout } = useLogout(company.slug);
  const { handleCompanySaved } = useHandleCompanySaved(company.slug);

  const profileDrawer = useRef<ElementRef<typeof ProfileDrawer>>(null);
  const [companyDrawerOpen, setCompanyDrawerOpen] = useState(
    () => !company.name,
  );
  const companyDrawer = useRef<ElementRef<typeof Drawer>>(null);
  // Seeds the settings form from Firestore, not from the (possibly cached) prop.
  const { freshCompany } = useFreshCompany(company, companyDrawerOpen);

  if (!company.name && companyDrawerOpen !== true) {
    setCompanyDrawerOpen(true);
  }

  useEffect(() => {
    if (!company.name) {
      const timeout = setTimeout(() => {
        companyDrawer.current?.openDrawer();
      }, 1);

      return () => clearTimeout(timeout);
    }
  }, [company.name]);

  return (
    <>
      <Menu
        Button={
          <motion.div
            transition={{ duration: 0.25 }}
            style={{ opacity: 0 }}
            animate={{ opacity: showAvatar ? 1 : 0 }}
          >
            <div className="block h-[37px] w-[37px] rounded-full border border-black/40 shadow-md dark:border-white/40 sm:h-[42px] sm:w-[42px]">
              <Image
                alt="Avatar"
                src={user.profile.avatar || imageSrc}
                className="!h-full !w-full cursor-pointer overflow-hidden rounded-full bg-white"
                onLoad={() => setShowAvatar(true)}
                preload
                width={40}
                height={40}
              />
            </div>
          </motion.div>
        }
        minWidth={210}
      >
        <button
          type="button"
          className="flex h-full w-full items-center p-3"
          onClick={() => profileDrawer.current?.openDrawer()}
        >
          <UserIcon />
          <span className="ml-2">{user.email}</span>
        </button>

        {canCreateStories && (
          <Link href={`/${company.slug}/library`}>
            <span className="flex h-full w-full items-center p-3">
              <LibraryIcon />
              <span className="ml-2">My Library</span>
            </span>
          </Link>
        )}

        {canEditUsers && (
          <Link
            href={{
              pathname: `/${company.slug}/team`,
              query: { tab: TeamsTab.USERS },
            }}
          >
            <span className="flex h-full w-full items-center p-3">
              <TeamIcon />
              <span className="ml-2">Team</span>
            </span>
          </Link>
        )}

        {canEditUsers && (
          <button
            type="button"
            className="flex h-full w-full items-center p-3"
            onClick={() => {
              setCompanyDrawerOpen(true);
              setTimeout(() => {
                companyDrawer.current?.openDrawer();
              }, 1);
            }}
          >
            <CompanyIcon size={18} />
            <span className="ml-2">Company</span>
          </button>
        )}

        {canEditUsers && (
          <Link
            href={{
              pathname: `/${company.slug}/statistics`,
            }}
          >
            <span className="flex h-full w-full items-center p-3">
              <StatisticsIcon size={18} />
              <span className="ml-2">Statistics</span>
            </span>
          </Link>
        )}

        <button
          type="button"
          onClick={handleLogout}
          className="flex h-full w-full items-center p-3"
        >
          <LogoutIcon />
          <span className="ml-2">Logout</span>
        </button>
      </Menu>

      <ProfileDrawer ref={profileDrawer} user={user} company={company} />

      {companyDrawerOpen && (
        <Drawer
          ref={companyDrawer}
          onClose={() => {
            setCompanyDrawerOpen(false);
            companyDrawer.current?.closeDrawer();
            if (!user.profile.firstName) {
              profileDrawer.current?.openDrawer();
            }
          }}
        >
          {freshCompany ? (
            <Formik
              initialValues={{
                company: {
                  name: freshCompany.name,
                  domain: freshCompany.domain,
                  slug: freshCompany.slug,
                  private: freshCompany.private,
                  galleryAccessKey: freshCompany.galleryAccessKey ?? '',
                  theming: { colors: freshCompany.theming?.colors || [] },
                },
                selectedColor: null,
                uploadedFileLight: null,
                uploadedFileDark: null,
              }}
              onSubmit={() => {}}
            >
              <CompanyDrawer
                company={freshCompany}
                onClose={() => {
                  setCompanyDrawerOpen(false);
                  companyDrawer.current?.closeDrawer();
                  if (!user.profile.firstName) {
                    profileDrawer.current?.openDrawer();
                  }
                }}
                onSubmit={slug => {
                  setCompanyDrawerOpen(false);
                  companyDrawer.current?.closeDrawer();
                  handleCompanySaved(slug);
                }}
              />
            </Formik>
          ) : (
            <div className="flex h-full items-center justify-center">
              <Spinner />
            </div>
          )}
        </Drawer>
      )}
    </>
  );
};
